import { Phone, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Card } from '@/components/common/Card';
import corporateTheme from '@/theme/corporateTheme';

interface CallStats {
  total: number;
  scheduled: number;
  completed: number;
  overdue: number;
  dueToday: number;
}

interface CallStatsCardsProps {
  stats: CallStats;
  variant?: 'default' | 'compact';
}

export default function CallStatsCards({ stats, variant = 'default' }: CallStatsCardsProps) {
  const isCompact = variant === 'compact';

  const statItems = [
    {
      label: 'Total Calls',
      value: stats.total,
      icon: Phone,
      color: 'text-slate-700',
      bgColor: 'bg-slate-50',
      iconBg: 'bg-slate-100',
    },
    {
      label: 'Scheduled',
      value: stats.scheduled,
      icon: Clock,
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      iconBg: 'bg-blue-100',
    },
    {
      label: 'Completed',
      value: stats.completed,
      icon: CheckCircle,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
    },
    {
      label: 'Overdue',
      value: stats.overdue,
      icon: AlertCircle,
      color: 'text-red-700',
      bgColor: 'bg-red-50',
      iconBg: 'bg-red-100',
      highlight: stats.overdue > 0,
    },
  ];

  if (isCompact) {
    return (
      <Card className="p-4 border border-gray-200">
        <div className="grid grid-cols-4 gap-4">
          {statItems.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${item.iconBg}`}>
                  <Icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div>
                  <div className="text-sm text-gray-600">{item.label}</div>
                  <div className={`text-xl font-bold ${item.color}`}>
                    {item.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {statItems.map((item) => {
        const Icon = item.icon;
        return (
          <Card 
            key={item.label} 
            className={`p-4 border ${item.highlight ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">{item.label}</p>
                <p className={`text-2xl font-bold ${item.color} mt-1`}>
                  {item.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${item.iconBg}`}>
                <Icon className={`w-6 h-6 ${item.color}`} />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
